import type { CSSProperties, SVGAttributes } from 'react';

import { cn } from '@/shared/lib/utils';

/**
 * Phoenix icon set — Lucide-style 24×24 single-stroke glyphs.
 * Paths are encoded as pipe-separated `d` strings so each glyph is a single
 * data row rather than a JSX subtree. Add new icons by extending PHX_ICONS;
 * the literal type derived below gives consumers full IDE autocomplete.
 */
export const PHX_ICONS = {
  search: 'M9 9m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0|M21 21l-4.35-4.35',
  chevdown: 'M6 9l6 6 6-6',
  chevright: 'M9 6l6 6-6 6',
  chevleft: 'M15 6l-6 6 6 6',
  chevup: 'M6 15l6-6 6 6',
  x: 'M18 6L6 18|M6 6l12 12',
  plus: 'M12 5v14|M5 12h14',
  minus: 'M5 12h14',
  check: 'M5 12l5 5L20 7',
  edit: 'M11 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6|M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:
    'M3 6h18|M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2|M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6',
  save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z|M17 21v-8H7v8|M7 3v5h8',
  filter: 'M22 3H2l8 9.46V19l4 2v-8.54L22 3z',
  sort: 'M3 6h18|M6 12h12|M10 18h4',
  more: 'M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0|M19 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0|M5 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
  morev:
    'M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0|M12 5m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0|M12 19m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0',
  alert:
    'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z|M12 9v4|M12 17h.01',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M12 16v-4|M12 8h.01',
  ok: 'M22 11.08V12a10 10 0 1 1-5.93-9.14|M22 4L12 14.01l-3-3',
  open: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6|M15 3h6v6|M10 14L21 3',
  plane: 'M16 3.5L20 7.5L12 15.5L10 21L8.5 19.5L11 16L4 11L5.5 9.5L13.5 11L17.5 7L16 3.5z',
  bus: 'M4 17h16M6 17V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10M9 21v-2M15 21v-2M7 11h10M8 14h.01M16 14h.01',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2|M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z|M23 21v-2a4 4 0 0 0-3-3.87|M16 3.13a4 4 0 0 1 0 7.75',
  calendar:
    'M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z|M16 2v4|M8 2v4|M3 10h18',
  map: 'M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z|M8 2v16|M16 6v16',
  star: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z',
  pin: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z|M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  layers: 'M12 2l-10 5 10 5 10-5-10-5z|M2 17l10 5 10-5|M2 12l10 5 10-5',
  folder: 'M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z',
  detach: 'M15 3h6v6|M10 14L21 3|M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  dock: 'M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4|M10 17l5-5-5-5|M15 12H3',
  maximize: 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z',
  restore:
    'M8 8V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2|M4 10h10a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z',
  lock: 'M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z|M8 11V7a4 4 0 0 1 8 0v4',
  unlock:
    'M5 11h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z|M8 11V7a4 4 0 0 1 7.5-2',
  zoomin: 'M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0|M21 21l-4.35-4.35|M11 8v6|M8 11h6',
  zoomout: 'M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0|M21 21l-4.35-4.35|M8 11h6',
  fullscr: 'M3 7V3h4|M21 7V3h-4|M3 17v4h4|M21 17v4h-4',
  notif: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9|M13.73 21a2 2 0 0 1-3.46 0',
  help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3|M12 17h.01',
  arrowR: 'M5 12h14|M12 5l7 7-7 7',
  trend: 'M23 6l-9.5 9.5-5-5L1 18|M17 6h6v6',
  globe:
    'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M2 12h20|M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10|M12 2a15.3 15.3 0 0 0-4 10 15.3 15.3 0 0 0 4 10',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4|M7 10l5 5 5-5|M12 15V3',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4|M17 8l-5-5-5 5|M12 3v12',
  copy: 'M20 9h-9a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2z|M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1',
  settings:
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z|M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2|M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z|M9 22V12h6v10',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z|M22 6l-10 7L2 6',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z|M12 6v6l4 2',
  refresh:
    'M23 4v6h-6|M1 20v-6h6|M3.51 9a9 9 0 0 1 14.85-3.36L23 10|M1 14l4.64 4.36A9 9 0 0 0 20.49 15',
  link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71|M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  'external-link': 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6|M15 3h6v6|M10 14 21 3',
  sun: 'M12 8m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0|M12 2v2|M12 20v2|M4.93 4.93l1.41 1.41|M17.66 17.66l1.41 1.41|M2 12h2|M20 12h2|M6.34 17.66l-1.41 1.41|M19.07 4.93l-1.41 1.41',
  moon: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z',
  // Oko / oko přeškrtnuté — zobrazit/skrýt heslo (Tabler eye / eye-off).
  eye: 'M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0|M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6',
  'eye-off':
    'M10.585 10.587a2 2 0 0 0 2.829 2.828|M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87|M3 3l18 18',
  // Doménové type-ikony hlavního menu (24×24, stroke 1.75) — portováno z prototypu menu icons.jsx.
  building:
    'M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z|M9 7h2|M13 7h2|M9 11h2|M13 11h2|M9 15h2|M13 15h2',
  bed: 'M3 8v12|M3 14h18v6|M21 20v-5a3 3 0 0 0-3-3H9|M5.1 10.5a1.4 1.4 0 1 0 2.8 0a1.4 1.4 0 1 0-2.8 0',
  flight: 'M22 2L11 13|M22 2l-7 20-4-9-9-4 20-7z',
  train:
    'M8 3h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z|M6 10h12|M9 20l-2 2|M15 20l2 2|M8.1 13.3a0.9 0.9 0 1 0 1.8 0a0.9 0.9 0 1 0-1.8 0|M14.1 13.3a0.9 0.9 0 1 0 1.8 0a0.9 0.9 0 1 0-1.8 0',
  ship: 'M3 14l1.4 5a2 2 0 0 0 1.9 1.4h11.4a2 2 0 0 0 1.9-1.4L21 14|M5 14V8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v6|M12 3v3',
  car: 'M5 13l1.4-4.4A2 2 0 0 1 8.3 7h7.4a2 2 0 0 1 1.9 1.6L19 13|M4.5 13h15a1.5 1.5 0 0 1 1.5 1.5v2a1.5 1.5 0 0 1-1.5 1.5H4.5A1.5 1.5 0 0 1 3 16.5v-2A1.5 1.5 0 0 1 4.5 13z|M6.3 18.5a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0|M15.3 18.5a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0',
  coin: 'M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0-17 0|M14.6 9.4A2.8 2.5 0 0 0 12 8.1c-1.6 0-2.6.8-2.6 1.9 0 2.6 5.3 1.4 5.3 4 0 1.2-1.1 2-2.7 2a3 3 0 0 1-2.6-1.3|M12 6.6v11',
  cog: 'M9 12a3 3 0 1 0 6 0a3 3 0 1 0-6 0|M12 2.5v3|M12 18.5v3|M2.5 12h3|M18.5 12h3|M5.2 5.2l2.1 2.1|M16.7 16.7l2.1 2.1|M18.8 5.2l-2.1 2.1|M7.3 16.7l-2.1 2.1',
  doc: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z|M14 3v5h5|M9 13h6|M9 16.5h4',
  tag: 'M20.6 13.4 11 3.8A2 2 0 0 0 9.6 3H5a2 2 0 0 0-2 2v4.6a2 2 0 0 0 .6 1.4l9.6 9.6a2 2 0 0 0 2.8 0l4.6-4.6a2 2 0 0 0 0-2.8z|M6.3 7.6a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0-2.6 0',
  list: 'M8 6h12|M8 12h12|M8 18h12|M4 6h.01|M4 12h.01|M4 18h.01',
  // Vykřičník (helpdesk) — plain „!", bez kolečka (jako legacy Magic helpdesk).
  exclamation: 'M12 4v10|M12 18h.01',
  // Klíč (nástroje).
  wrench:
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  // Lebka — destruktivní operace (restart aplikace, ukončení démonů). Zaoblená lebka + 2 přední zuby
  // + oční důlky (prstence; při malé velikosti působí plně). Vzor: Tabler „skull".
  skull:
    'M12 4c4.418 0 8 3.358 8 7.5c0 1.901-.755 3.637-2 4.96v2.54a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-2.54c-1.245-1.322-2-3.058-2-4.96C4 7.358 7.582 4 12 4z|M10 17v3M14 17v3|M7.8 12a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0|M13.8 12a1.2 1.2 0 1 0 2.4 0a1.2 1.2 0 1 0-2.4 0',
  // Odhlášení (lucide log-out) — dveře + šipka ven.
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4|M16 17l5-5-5-5|M21 12H9',
  // Letadlo shora (trup + křídla) — rychlý filtr dopravy, portováno z DS prototypu tree-view.
  'plane-top':
    'M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z',
  // Autobus bez koleček — rychlý filtr dopravy, portováno z DS prototypu tree-view.
  'bus-plain': 'M4 17h16M6 17V7a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v10M9 21v-2M15 21v-2M7 11h10',
} as const;

export type PhxIconName = keyof typeof PHX_ICONS;

export interface IconProps extends Omit<SVGAttributes<SVGSVGElement>, 'name'> {
  name: PhxIconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Renders a single Phoenix icon.  Default size is 14px and stroke width 1.75,
 * matching the design system's compact density.  The icon inherits the
 * surrounding text colour (set `color: …` on the parent or pass an explicit
 * `style.color`).
 */
export function Icon({
  name,
  size = 14,
  strokeWidth = 1.75,
  className,
  style,
  ...rest
}: IconProps) {
  const path = PHX_ICONS[name];
  if (!path) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn('phx-icon inline-block shrink-0 align-[-2px]', className)}
      style={style}
      {...rest}
    >
      {path.split('|').map((d, index) => (
        <path key={index} d={d} />
      ))}
    </svg>
  );
}
